import { ApiVersionEnum } from './enums/ApiVersionEnum.js'
import {
	Http,
	isDebugMode,
	isPreProductionMode,
} from '@ipevo-rd/service-web-utils'

/**
 * Get base API URL
 * @return {string}
 */
export const getBaseAPIUrl = (): string =>
	// 如果是Debug Mode
	isDebugMode()
		? `https://dev-suncake-api.ipevo.com`
		: // 如果是預備上線模式
		isPreProductionMode()
		? `https://pp2-api.vurbo.ai`
		: // 正式環境
		  `https://live-api.vurbo.ai`

/**
 * Get API URL
 * @param apiVersion {ApiVersionEnum} API version
 * @return {string} API URL
 */
export const getAPIUrl = (apiVersion: ApiVersionEnum): string =>
	`${getBaseAPIUrl()}/${apiVersion}`

/**
 * Get viewer base URL（Cloudflare 獨立 domain，與 REST API domain 分離）
 * @return {string} viewer base URL
 */
export const getViewerBaseUrl = (): string =>
	// 如果是Debug Mode
	isDebugMode()
		? `https://dev-suncake-viewer.vurbo.ai`
		: // 如果是預備上線模式
		isPreProductionMode()
		? `https://pp2-suncake-viewer.vurbo.ai`
		: // 正式環境
		  `https://live-suncake-viewer.vurbo.ai`

/**
 * Get viewer API URL（搭配 `getViewerBaseUrl`，供 SSE 連線使用）
 * @param apiVersion {ApiVersionEnum} API version
 * @return {string} viewer API URL
 */
export const getViewerAPIUrl = (apiVersion: ApiVersionEnum): string =>
	`${getViewerBaseUrl()}/${apiVersion}`

/**
 * Get MEB licence auth base URL（登入打 MEB 取得 token 用）
 * @return {string} MEB base URL
 */
export const getLicenseBaseUrl = (): string =>
	// 如果是 Debug Mode
	isDebugMode()
		? 'https://dev-meb-api.ipevo.com'
		: // 如果是預備上線模式
		isPreProductionMode()
		? 'https://api-pp2-meb.ipevo.com'
		: // 正式環境
		  'https://api-meb.ipevo.com'

/**
 * Get HTTP instance
 * @param apiVersion {ApiVersionEnum} API version
 * @param timeout Timeout
 * @return {Http} HTTP instance
 */
export const getHttpInstance = (
	apiVersion: ApiVersionEnum,
	timeout = 80000,
): Http => new Http(getAPIUrl(apiVersion), timeout)

/**
 * Get Voice URL
 * @param sessionID {string} Session ID
 * @param selectTranslateLang {string} Selected translate language
 * @param voiceGender {string} Voice Gender
 * @return {string} Voice URL
 */
export const getVoiceUrl = (
	sessionID: string,
	selectTranslateLang: string,
	voiceGender: string,
): string =>
	`${getBaseAPIUrl()}/audio/${sessionID}/${selectTranslateLang}/${voiceGender}/index.m3u8`

/**
 * 取得 TSS MP4 URL
 * @param sessionID {string} Session ID
 * @param selectTranslateLang {string} Selected translate language
 * @param voiceGender {string} Voice Gender
 * @param messageID {string} Message ID
 * @return {string} Voice URL
 */
export const getVoiceMp4Url = (
	sessionID: string,
	selectTranslateLang: string,
	voiceGender: string,
	messageID: string,
): string =>
	`${getBaseAPIUrl()}/audio/${sessionID}/${selectTranslateLang}/${voiceGender}/${messageID}.mp4`

/**
 * 取得 TSS MP3 URL
 * @param sessionID {string} Session ID
 * @param selectTranslateLang {string} Selected translate language
 * @param voiceGender {string} Voice Gender
 * @param messageID {string} Message ID
 * @return {string} Voice URL
 */
export const getVoiceMp3Url = (
	sessionID: string,
	selectTranslateLang: string,
	voiceGender: string,
	messageID: string,
): string =>
	`${getBaseAPIUrl()}/audio/${sessionID}/${selectTranslateLang}/${voiceGender}/${messageID}.mp3`
